import React from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ChartAxis,
  ChartMarker,
  ChartPane,
  ChartSeries,
  ChartSpec,
  ChartStyle,
} from "./types";
import { chartTimeRange, formatChartTime } from "./timeRange";

const DEFAULT_COLORS = [
  "#60A5FA",
  "#A78BFA",
  "#22C55E",
  "#F59E0B",
  "#F472B6",
  "#22D3EE",
];

function formatNumber(value: unknown, format: ChartAxis["format"]): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  if (format === "currency") {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(number);
  }
  if (format === "percent") return `${number.toFixed(2)}%`;
  if (format === "compact") {
    return new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(number);
  }
  if (format === "integer") return Math.round(number).toLocaleString();
  return number.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatX(
  value: unknown,
  type: ChartSpec["xAxis"]["type"],
  range: ReturnType<typeof chartTimeRange>,
  timezone?: string,
  detail = false,
): string {
  if (type !== "time") return String(value ?? "");
  return formatChartTime(value, range, timezone, detail);
}

function dashArray(dash: ChartStyle["dash"]): string | undefined {
  if (dash === "dashed") return "7 5";
  if (dash === "dotted") return "2 4";
  return undefined;
}

function CandlestickShape(props: any): React.ReactElement | null {
  const { x, y, width, height, payload, series } = props;
  const mapping = series.fieldMapping;
  const open = Number(payload[mapping.open]);
  const high = Number(payload[mapping.high]);
  const low = Number(payload[mapping.low]);
  const close = Number(payload[mapping.close]);
  if (![x, y, width, height, open, high, low, close].every(Number.isFinite)) {
    return null;
  }
  const range = Math.max(high - low, Number.EPSILON);
  const openY = y + ((high - open) / range) * height;
  const closeY = y + ((high - close) / range) * height;
  const bodyY = Math.min(openY, closeY);
  const bodyHeight = Math.max(Math.abs(closeY - openY), 2);
  const positive = close >= open;
  const color = positive
    ? series.style?.positiveColor || "#22C55E"
    : series.style?.negativeColor || "#EF4444";
  const center = x + width / 2;
  const bodyWidth = Math.max(3, width * 0.62);
  return (
    <g
      data-series-mark="candlestick"
      data-series-id={series.id}
      data-semantic-element-id={series.semanticElementId}
    >
      <line
        x1={center}
        x2={center}
        y1={y}
        y2={y + height}
        stroke={color}
        strokeWidth={1.5}
      />
      <rect
        x={center - bodyWidth / 2}
        y={bodyY}
        width={bodyWidth}
        height={bodyHeight}
        rx={1}
        fill={color}
      />
    </g>
  );
}

function markerValue(spec: ChartSpec, marker: ChartMarker): number | null {
  if (typeof marker.value === "number") return marker.value;
  const row = spec.data.find(
    (candidate) => candidate[spec.xAxis.key] === marker.x,
  );
  const series = spec.series.find(
    (candidate) => candidate.id === marker.seriesId,
  );
  if (!row || !series) return null;
  const key =
    series.mark === "candlestick" ? series.fieldMapping?.close : series.dataKey;
  const value = key ? Number(row[key]) : Number.NaN;
  return Number.isFinite(value) ? value : null;
}

function MarkerLabel(props: any): React.ReactElement {
  const { cx, cy, marker, labelAlignment = "middle" } = props;
  const offset =
    marker.placement === "above"
      ? -18
      : marker.placement === "below"
        ? 24
        : -12;
  const label = String(marker.label);
  const width = Math.max(42, label.length * 6.5 + 14);
  const labelY = cy + offset;
  const rectX =
    labelAlignment === "end"
      ? cx - width
      : labelAlignment === "start"
        ? cx
        : cx - width / 2;
  const textX =
    labelAlignment === "end"
      ? cx - width / 2
      : labelAlignment === "start"
        ? cx + width / 2
        : cx;
  return (
    <g
      data-chart-marker={marker.id}
      data-semantic-element-id={marker.semanticElementId}
    >
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill={marker.color}
        stroke="#08111F"
        strokeWidth={2}
      />
      <rect
        x={rectX}
        y={labelY - 13}
        width={width}
        height={18}
        rx={5}
        fill="#08111F"
        stroke={marker.color}
        strokeWidth={1}
        data-chart-label-background={marker.id}
        data-testid={`chart-label-background-${marker.id}`}
      />
      <text
        x={textX}
        y={labelY}
        fill="#F8FAFC"
        fontSize={11}
        fontWeight={750}
        textAnchor="middle"
      >
        {label}
      </text>
    </g>
  );
}

function ReferenceLabel(props: any): React.ReactElement | null {
  const {
    viewBox,
    value,
    color,
    id,
    semanticElementId,
    position = "end",
  } = props;
  const x = Number(viewBox?.x ?? viewBox?.x1);
  const y = Number(viewBox?.y);
  const alternateY = Number(viewBox?.y1);
  const chartWidth = Number(
    viewBox?.width ??
      (Number.isFinite(Number(viewBox?.x2))
        ? Math.abs(Number(viewBox.x2) - Number(viewBox?.x1))
        : Number.NaN),
  );
  const resolvedY = Number.isFinite(y) ? y : alternateY;
  if (![x, resolvedY, chartWidth].every(Number.isFinite)) return null;
  const label = String(value);
  const width = Math.max(42, label.length * 6.5 + 14);
  const left = x + 6;
  const right = x + chartWidth - 6;
  const anchor = position === "start" ? left : right;
  const rectX = position === "start" ? anchor : anchor - width;
  return (
    <g
      data-chart-reference-label={id}
      data-semantic-element-id={semanticElementId}
    >
      <rect
        x={rectX}
        y={resolvedY - 18}
        width={width}
        height={18}
        rx={5}
        fill="#08111F"
        stroke={color}
        strokeWidth={1}
      />
      <text
        x={rectX + width / 2}
        y={resolvedY - 5}
        fill="#F8FAFC"
        fontSize={11}
        fontWeight={700}
        textAnchor="middle"
      >
        {label}
      </text>
    </g>
  );
}

function renderSeries(series: ChartSeries, index: number): React.ReactNode {
  const color =
    series.style?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  const common = {
    yAxisId: series.axisId,
    name: series.label,
    stroke: color,
    fill: series.style?.fill || color,
    opacity: series.style?.opacity ?? 1,
    isAnimationActive: false,
    "data-series-id": series.id,
    "data-semantic-element-id": series.semanticElementId,
  };

  if (series.mark === "candlestick") {
    const mapping = series.fieldMapping!;
    return (
      <Bar
        key={series.id}
        {...common}
        dataKey={(row: Record<string, unknown>) => [
          Number(row[mapping.low]),
          Number(row[mapping.high]),
        ]}
        shape={(props: any) => <CandlestickShape {...props} series={series} />}
        maxBarSize={22}
      />
    );
  }
  if (series.mark === "bar") {
    return (
      <Bar
        key={series.id}
        {...common}
        dataKey={series.dataKey!}
        maxBarSize={34}
        radius={[3, 3, 0, 0]}
        data-series-mark="bar"
      />
    );
  }
  if (series.mark === "area") {
    return (
      <Area
        key={series.id}
        {...common}
        dataKey={series.dataKey!}
        type="monotone"
        strokeWidth={series.style?.lineWidth || 2}
        fillOpacity={series.style?.opacity ?? 0.18}
        data-series-mark="area"
      />
    );
  }
  if (series.mark === "scatter") {
    return (
      <Scatter
        key={series.id}
        {...common}
        dataKey={series.dataKey!}
        data-series-mark="scatter"
      />
    );
  }
  return (
    <Line
      key={series.id}
      {...common}
      dataKey={series.dataKey!}
      type="monotone"
      strokeWidth={series.style?.lineWidth || 2}
      strokeDasharray={dashArray(series.style?.dash || "solid")}
      dot={false}
      activeDot={{ r: 4 }}
      data-series-mark="line"
    />
  );
}

type TimeSeriesPlotProps = {
  spec: ChartSpec;
  pane: ChartPane;
  height: number;
  showXAxis: boolean;
};

export default function TimeSeriesPlot({
  spec,
  pane,
  height,
  showXAxis,
}: TimeSeriesPlotProps): React.ReactElement {
  const axes = spec.axes.filter((axis) => pane.axisIds.includes(axis.id));
  const series = spec.series.filter((item) => item.paneId === pane.id);
  const references = (spec.referenceLines || []).filter(
    (line) => line.paneId === pane.id,
  );
  const markers = (spec.markers || []).filter(
    (marker) => marker.paneId === pane.id,
  );
  const timeRange = chartTimeRange(spec);

  return (
    <div
      className="unified-chart__pane"
      data-pane-id={pane.id}
      style={{ height }}
    >
      {pane.label && (
        <span className="unified-chart__pane-label">{pane.label}</span>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={spec.data}
          margin={{ top: 24, right: 8, bottom: showXAxis ? 5 : 0, left: 0 }}
          syncId="unified-chart"
        >
          <CartesianGrid
            stroke="rgba(100, 116, 139, 0.16)"
            strokeDasharray="3 5"
            vertical={false}
          />
          <XAxis
            dataKey={spec.xAxis.key}
            type={spec.xAxis.type === "number" ? "number" : "category"}
            hide={!showXAxis}
            tickFormatter={(value) =>
              formatX(
                value,
                spec.xAxis.type,
                timeRange,
                spec.xAxis.timezone,
              )
            }
            tick={{ fill: "#8190A5", fontSize: 10 }}
            axisLine={{ stroke: "#27344A" }}
            tickLine={false}
            minTickGap={28}
          />
          {axes.map((axis) => (
            <YAxis
              key={axis.id}
              yAxisId={axis.id}
              orientation={axis.side}
              domain={[
                axis.domain?.min ?? (axis.domain?.includeZero ? 0 : "auto"),
                axis.domain?.max ?? "auto",
              ]}
              tickFormatter={(value) => formatNumber(value, axis.format)}
              tick={{ fill: "#8190A5", fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={56}
              mirror
            />
          ))}
          <Tooltip
            contentStyle={{
              background: "#0B1220",
              border: "1px solid #2A3850",
              borderRadius: 8,
              color: "#E2E8F0",
              fontSize: 12,
            }}
            labelFormatter={(value) =>
              formatX(
                value,
                spec.xAxis.type,
                timeRange,
                spec.xAxis.timezone,
                true,
              )
            }
          />
          {series.map(renderSeries)}
          {references.map((line) => {
            const segment:
              | [
                  { x: string | number; y: number },
                  { x: string | number; y: number },
                ]
              | undefined =
              line.xStart !== undefined && line.xEnd !== undefined
                ? [
                    { x: line.xStart, y: line.value },
                    { x: line.xEnd, y: line.value },
                  ]
                : undefined;
            return (
              <ReferenceLine
                key={line.id}
                yAxisId={line.axisId}
                {...(segment ? { segment } : { y: line.value })}
                stroke={line.style?.color || "#94A3B8"}
                strokeWidth={line.style?.lineWidth || 1}
                strokeOpacity={line.style?.opacity ?? 1}
                strokeDasharray={dashArray(line.style?.dash || "dashed")}
                ifOverflow="extendDomain"
                label={{
                  value: line.label,
                  content: (props: any) => (
                    <ReferenceLabel
                      {...props}
                      id={line.id}
                      semanticElementId={line.semanticElementId}
                      color={line.style?.color || "#94A3B8"}
                      position={line.labelPosition || "end"}
                    />
                  ),
                }}
              />
            );
          })}
          {markers.map((marker) => {
            const value = markerValue(spec, marker);
            const targetSeries = spec.series.find(
              (candidate) => candidate.id === marker.seriesId,
            );
            if (value === null || !targetSeries) return null;
            const markerIndex = spec.data.findIndex(
              (candidate) => candidate[spec.xAxis.key] === marker.x,
            );
            const markerPosition =
              spec.data.length > 1 ? markerIndex / (spec.data.length - 1) : 0.5;
            const labelAlignment =
              markerPosition >= 0.8
                ? "end"
                : markerPosition <= 0.2
                  ? "start"
                  : "middle";
            return (
              <ReferenceDot
                key={marker.id}
                x={marker.x}
                y={value}
                yAxisId={targetSeries.axisId}
                ifOverflow="visible"
                shape={(props: any) => (
                  <MarkerLabel
                    {...props}
                    marker={marker}
                    labelAlignment={labelAlignment}
                  />
                )}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
